import { HostDisintermediationRiskLevel } from "@ps-design-system/components/HostDisintermediation";

import sharedAnalyticsIntegrations from "../../shared-components/analytics-integrations/analytics-utils";

declare const analytics: {
  track: (event: string, properties: Record<string, unknown>) => void;
};

export type TodoItemParameter =
  | "review_bookings"
  | "booking_update_request"
  | "unread_messages"
  | "slow_response_warning"
  | "paused_listing"
  | "vat_id_attention";

export type RecommendedItemParameter =
  | "unread_reviews"
  | "submit_guest_review"
  | "add_video_tour"
  | "event_faq"
  | "calendar_sync"
  | "enable_instant_book";

type BaseProperties = {
  ssoid?: string;
};

const track = (event: string, properties: Record<string, unknown>) => {
  if (!sharedAnalyticsIntegrations.segmentLoaded()) return;
  analytics.track(event, properties);
};

export const trackHostHubViewed = ({ ssoid }: BaseProperties) => {
  track("host_hub_viewed", { ssoid });
};

export const trackTodoItemClicked = ({
  parameter,
  ssoid,
  ...extra
}: BaseProperties & {
  parameter: TodoItemParameter;
  $_value_shown?: number;
  unread_count?: number;
}) => {
  track("todo_item_clicked", { parameter, ssoid, ...extra });
};

export const trackRecommendedItemClicked = ({
  parameter,
  ssoid,
}: BaseProperties & { parameter: RecommendedItemParameter }) => {
  track("recommended_item_clicked", { parameter, ssoid });
};

export const trackRecommendedItemDismissed = ({
  parameter,
  ssoid,
}: BaseProperties & { parameter: RecommendedItemParameter }) => {
  track("recommended_item_dismissed", { parameter, ssoid });
};

export const trackUpcomingBookingClicked = ({ ssoid }: BaseProperties) => {
  track("upcoming_booking_clicked", { ssoid });
};

export const trackUpcomingViewAllClicked = ({ ssoid }: BaseProperties) => {
  track("upcoming_view_all_clicked", { ssoid });
};

export const trackPromotedByHostBannerViewed = ({ ssoid }: BaseProperties) => {
  track("promoted_by_host_banner_viewed", { ssoid });
};

export const trackPromotedByHostBannerClicked = ({ ssoid }: BaseProperties) => {
  track("promoted_by_host_banner_clicked", { ssoid });
};

export const trackVendorRecommendationBannerClicked = ({
  ssoid,
}: BaseProperties) => {
  track("vendor_recommendation_banner_clicked", { ssoid });
};

export const trackReferVendorButtonClicked = ({
  buttonName,
  buttonLocation,
}: {
  buttonName: string;
  buttonLocation: string;
}) => {
  track("button clicked", {
    button_name: buttonName,
    button_location: buttonLocation,
  });
};

export const trackPromotedByHostBannerDismissed = ({
  ssoid,
}: BaseProperties) => {
  track("promoted_by_host_banner_dismissed", { ssoid });
};

export const trackVendorRecommendationBannerDismissed = ({
  ssoid,
}: BaseProperties) => {
  track("vendor_recommendation_banner_dismissed", { ssoid });
};

type HostHubCalloutProperties = BaseProperties & {
  /** The callout's `type` from the `host_hub_marketing_banners` config. */
  calloutType: string;
};

export const trackHostHubCalloutViewed = ({
  calloutType,
  ssoid,
}: HostHubCalloutProperties) => {
  track("host_hub_callout_viewed", { ssoid, callout_type: calloutType });
};

export const trackHostHubCalloutClicked = ({
  calloutType,
  ssoid,
}: HostHubCalloutProperties) => {
  track("host_hub_callout_clicked", { ssoid, callout_type: calloutType });
};

export const trackHostHubCalloutDismissed = ({
  calloutType,
  ssoid,
}: HostHubCalloutProperties) => {
  track("host_hub_callout_dismissed", { ssoid, callout_type: calloutType });
};

export const trackHostDisintermediationViewed = ({
  ssoid,
  level,
  location,
}: BaseProperties & {
  level: HostDisintermediationRiskLevel;
  location: "banner" | "sheet";
}) => {
  track("host disintermediation risk score viewed", {
    sso_id: ssoid,
    risk_level: level,
    location,
    timestamp: new Date().getTime(),
  });
};
